import {
  useCancelReservation,
  useConfirmReservation,
  useMarkArrival,
  useReactivateReservation,
  useRejectReservation,
  useSetReservationArrivalStatus,
} from '@/features/reservations/hooks/useReservationMutations';
import { useT } from '@/i18n';
import type { Reservation } from '@restaurant/api-client';
import { ArrivalStatus } from '@restaurant/types';
import { f7 } from 'framework7-react';

export function useReservationActionHandlers() {
  const t = useT();
  const confirmMutation = useConfirmReservation();
  const reactivateMutation = useReactivateReservation();
  const rejectMutation = useRejectReservation();
  const cancelMutation = useCancelReservation();
  const arrivalMutation = useMarkArrival();
  const setArrivalMutation = useSetReservationArrivalStatus();

  const handleConfirm = (reservation: Reservation) => {
    confirmMutation.mutate(
      { reservationId: reservation.id },
      {
        onSuccess: () =>
          f7.toast.show({ text: t.reservations.toast.confirmed, closeTimeout: 2000 }),
        onError: () => f7.dialog.alert(t.reservations.errors.confirm),
      },
    );
    f7.popover.close();
  };

  const handleReactivate = (reservation: Reservation) => {
    reactivateMutation.mutate(
      { reservationId: reservation.id },
      {
        onSuccess: () =>
          f7.toast.show({ text: t.reservations.toast.confirmed, closeTimeout: 2000 }),
        onError: () => f7.dialog.alert(t.reservations.errors.confirm),
      },
    );
    f7.popover.close();
  };

  const handleReject = (reservation: Reservation) => {
    f7.dialog.prompt(
      t.reservations.prompts.rejectReason,
      t.reservations.prompts.rejectTitle,
      (reason) => {
        rejectMutation.mutate(
          { reservationId: reservation.id, reason },
          {
            onSuccess: () =>
              f7.toast.show({ text: t.reservations.toast.rejected, closeTimeout: 2000 }),
            onError: () => f7.dialog.alert(t.reservations.errors.reject),
          },
        );
      },
    );
    f7.popover.close();
  };

  const handleCancel = (reservation: Reservation) => {
    f7.dialog.confirm(
      t.reservations.prompts.cancelConfirm,
      t.reservations.prompts.cancelTitle,
      () => {
        cancelMutation.mutate(
          { reservationId: reservation.id },
          {
            onSuccess: () =>
              f7.toast.show({ text: t.reservations.toast.cancelled, closeTimeout: 2000 }),
            onError: () => f7.dialog.alert(t.reservations.errors.cancel),
          },
        );
      },
    );
    f7.popover.close();
  };

  const handleArrival = (
    reservation: Reservation,
    status:
      | ArrivalStatus.ARRIVED
      | ArrivalStatus.SEATED
      | ArrivalStatus.IN_PROGRESS
      | ArrivalStatus.COMPLETED
      | ArrivalStatus.NO_SHOW,
    message: string,
  ) => {
    arrivalMutation.mutate(
      { reservationId: reservation.id, arrivalStatus: status },
      { onSuccess: () => f7.toast.show({ text: message, closeTimeout: 2000 }) },
    );
    f7.popover.close();
  };

  const handleMarkArrived = (reservation: Reservation) =>
    handleArrival(reservation, ArrivalStatus.ARRIVED, t.reservations.toast.arrived);

  const handleMarkSeated = (reservation: Reservation) =>
    handleArrival(reservation, ArrivalStatus.SEATED, t.reservations.toast.seated);

  const handleMarkInProgress = (reservation: Reservation) =>
    handleArrival(reservation, ArrivalStatus.IN_PROGRESS, t.reservations.toast.inProgress);

  const handleMarkCompleted = (reservation: Reservation) =>
    handleArrival(reservation, ArrivalStatus.COMPLETED, t.reservations.toast.completed);

  const handleMarkNoShow = (reservation: Reservation) =>
    handleArrival(reservation, ArrivalStatus.NO_SHOW, t.reservations.toast.noShow);

  const handleMarkNotStarted = (reservation: Reservation) => {
    setArrivalMutation.mutate(
      { reservationId: reservation.id, arrivalStatus: 'WAITING' },
      {
        onSuccess: () =>
          f7.toast.show({ text: t.reservations.displayStatus.waiting, closeTimeout: 2000 }),
      },
    );
    f7.popover.close();
  };

  const handleReschedule = (reservation: Reservation) => {
    setArrivalMutation.mutate(
      { reservationId: reservation.id, arrivalStatus: 'RESCHEDULED' },
      {
        onSuccess: () =>
          f7.toast.show({ text: t.reservations.displayStatus.rescheduled, closeTimeout: 2000 }),
      },
    );
    f7.popover.close();
  };

  return {
    handleConfirm,
    handleReactivate,
    handleReject,
    handleCancel,
    handleMarkArrived,
    handleMarkSeated,
    handleMarkInProgress,
    handleMarkCompleted,
    handleMarkNoShow,
    handleMarkNotStarted,
    handleReschedule,
  };
}
